// src/components/ui/DashboardHero/DashboardHero.jsx
import React from "react"
import { useSelector } from "react-redux"
import { cva } from "class-variance-authority"
import { cn } from "../../../lib/utils"

/**
 * DashboardHero — landing page hero banner.
 * Navy gradient + grain overlay, entrance animations.
 */
const dashboardHeroVariants = cva(
  "relative overflow-hidden bg-[linear-gradient(135deg,var(--hero-bg-start)_0%,var(--hero-bg-end)_100%)] px-6 pt-8 pb-7 max-[768px]:px-4 max-[768px]:pt-5 max-[768px]:pb-4",
)

/* Grain texture overlay via SVG noise at ~3% opacity */
const GRAIN_TEXTURE =
  "data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E"

function getTimeGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return "Good morning"
  if (hour < 18) return "Good afternoon"
  return "Good evening"
}

const DashboardHero = React.forwardRef(({ className, ...props }, ref) => {
  const permissions = useSelector((state) => state.rolePermission.permissions)
  const { userList } = useSelector((state) => state.user)

  const firstName =
    userList?.[0]?.first_name || permissions?.userInfo?.first_name || "there"

  // roleLabel is the display-only role name (e.g. "User Facility Admin"
  // shows as itself here even though it functions as Super Admin
  // everywhere else) -- falls back to role for permissions shapes that
  // predate roleLabel.
  const role = permissions?.roleLabel || permissions?.role || ""

  const greeting = getTimeGreeting()

  return (
    <div
      ref={ref}
      className={cn(dashboardHeroVariants(), className)}
      {...props}
    >
      <div className="relative z-[1] max-w-[1100px]">
        <p className="mb-1 animate-hero-fade text-[13px] font-medium text-[var(--hero-text-secondary)]">
          {greeting},
        </p>
        <h1 className="m-0 animate-hero-fade text-[28px] font-bold leading-[1.15] tracking-[-0.5px] text-[var(--text-on-dark-surface)] [animation-delay:0.05s] max-[768px]:text-[22px]">
          {firstName}
        </h1>
        {role && (
          <div className="mt-2 flex animate-hero-fade items-center gap-2 text-[12px] text-[var(--hero-text-tertiary)] [animation-delay:0.1s]">
            <span className="rounded-[10px] bg-[var(--hero-chip-bg)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--hero-chip-text)]">
              {role.replaceAll("_", " ")}
            </span>
          </div>
        )}
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[length:200px] opacity-50"
        style={{ backgroundImage: `url("${GRAIN_TEXTURE}")` }}
      />
    </div>
  )
})
DashboardHero.displayName = "DashboardHero"

export default DashboardHero
export { dashboardHeroVariants }
